import React, { useState, useMemo, useCallback } from 'react';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../utils';

// Combobox Context
const ComboboxContext = React.createContext();

export const useCombobox = () => {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('useCombobox must be used within a ComboboxProvider');
  }
  return context;
};

// Combobox Provider
export const ComboboxProvider = ({ 
  children, 
  data = [], 
  value, 
  onValueChange, 
  searchable = true,
  multiple = false,
  placeholder = "Select option...",
  emptyText = "No options found",
  className
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;
    
    return data.filter(item => {
      const searchableText = typeof item === 'string' 
        ? item 
        : item.label || item.name || item.title || '';
      return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [data, searchTerm, searchable]);

  // Handle selection
  const handleSelect = useCallback((selectedValue) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(selectedValue)
        ? currentValues.filter(v => v !== selectedValue)
        : [...currentValues, selectedValue];
      onValueChange?.(newValues);
    } else {
      onValueChange?.(selectedValue);
      setOpen(false);
    }
  }, [multiple, value, onValueChange]);

  // Check if item is selected
  const isSelected = useCallback((itemValue) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(itemValue);
    }
    return value === itemValue;
  }, [multiple, value]);

  const contextValue = {
    open,
    setOpen,
    searchTerm,
    setSearchTerm,
    data,
    filteredData,
    value,
    handleSelect,
    isSelected,
    searchable,
    multiple,
    placeholder,
    emptyText
  };

  return (
    <ComboboxContext.Provider value={contextValue}>
      <div className={cn("relative", className)}>
        {children}
      </div>
    </ComboboxContext.Provider>
  );
};

// Combobox Trigger
export const ComboboxTrigger = ({ className, children, ...props }) => {
  const { open, setOpen, placeholder, value, multiple, data } = useCombobox();

  const getDisplayValue = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        const item = data.find(d => d.value === value[0] || d === value[0]);
        return item?.label || item?.name || item || value[0];
      }
      return `${value.length} selected`;
    }
    
    if (value) {
      const item = data.find(d => d.value === value || d === value);
      return item?.label || item?.name || item || value;
    }
    
    return placeholder;
  };

  return (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn("w-full justify-between", className)}
      onClick={() => setOpen(!open)}
      type="button"
      {...props}
    >
      {children || getDisplayValue()}
      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );
};

// Combobox Content
export const ComboboxContent = ({ className, children, ...props }) => {
  const { open, searchable, searchTerm, setSearchTerm } = useCombobox();

  if (!open) return null;

  return (
    <div
      className={cn(
        "absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg",
        className
      )}
      {...props}
    >
      {searchable && (
        <div className="p-2 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      )}
      <div className="max-h-60 overflow-auto">
        {children}
      </div>
    </div>
  );
};

// Combobox List
export const ComboboxList = ({ className, children, ...props }) => (
  <div className={cn("p-1", className)} {...props}>
    {children}
  </div>
);

// Combobox Item
export const ComboboxItem = ({ 
  value, 
  children, 
  className, 
  disabled = false,
  ...props 
}) => {
  const { handleSelect, isSelected } = useCombobox();

  return (
    <div
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100",
        disabled && "opacity-50 cursor-not-allowed",
        isSelected(value) && "bg-blue-50 text-blue-600",
        className
      )}
      onClick={disabled ? undefined : () => handleSelect(value)}
      {...props}
    >
      {children}
      {isSelected(value) && (
        <CheckIcon className="ml-auto h-4 w-4" />
      )}
    </div>
  );
};

// Combobox Empty
export const ComboboxEmpty = ({ className, children, ...props }) => {
  const { emptyText } = useCombobox();
  
  return (
    <div className={cn("p-2 text-sm text-gray-500 text-center", className)} {...props}>
      {children || emptyText}
    </div>
  );
};

// Multi-select Combobox
export const ComboboxMulti = ({ 
  data = [], 
  value = [], 
  onValueChange, 
  placeholder = "Select options...",
  className,
  ...props 
}) => {
  return (
    <ComboboxProvider
      data={data}
      value={value}
      onValueChange={onValueChange}
      multiple={true}
      placeholder={placeholder}
      className={className}
    >
      <ComboboxTrigger />
      <ComboboxContent>
        <ComboboxList>
          {data.map((item, index) => {
            const itemValue = item.value || item;
            const itemLabel = item.label || item.name || item;
            
            return (
              <ComboboxItem key={index} value={itemValue}>
                {itemLabel}
              </ComboboxItem>
            );
          })}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxProvider>
  );
};

// Single-select Combobox
export const ComboboxSingle = ({ 
  data = [], 
  value, 
  onValueChange, 
  placeholder = "Select option...",
  className,
  ...props 
}) => {
  return (
    <ComboboxProvider
      data={data}
      value={value}
      onValueChange={onValueChange}
      multiple={false}
      placeholder={placeholder}
      className={className}
    >
      <ComboboxTrigger />
      <ComboboxContent>
        <ComboboxList>
          {data.map((item, index) => {
            const itemValue = item.value || item;
            const itemLabel = item.label || item.name || item;
            
            return (
              <ComboboxItem key={index} value={itemValue}>
                {itemLabel}
              </ComboboxItem>
            );
          })}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxProvider>
  );
};

// Search Combobox
export const ComboboxSearch = ({ 
  data = [], 
  value, 
  onValueChange, 
  placeholder = "Search and select...",
  className,
  ...props 
}) => {
  return (
    <ComboboxProvider
      data={data}
      value={value}
      onValueChange={onValueChange}
      searchable={true}
      placeholder={placeholder}
      className={className}
    >
      <ComboboxTrigger />
      <ComboboxContent>
        <ComboboxList>
          {data.map((item, index) => {
            const itemValue = item.value || item;
            const itemLabel = item.label || item.name || item;
            
            return (
              <ComboboxItem key={index} value={itemValue}>
                {itemLabel}
              </ComboboxItem>
            );
          })}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxProvider>
  );
};

// All components are already exported as named exports above
